import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Shield, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';
import api, { resolveFileUrl } from '../utils/api';

const Clubs = () => {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'

  const fetchClubs = useCallback(async () => {
    try {
      const response = await api.get('/clubs');
      setClubs(response.data);
    } catch (error) {
      toast.error('Failed to load clubs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClubs();
  }, [fetchClubs]);

  const filteredClubs = useMemo(() => {
    let filtered = clubs;
    if (searchTerm) {
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.league.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [clubs, searchTerm]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                Clubs
              </h1>
              <p className="text-lg text-zinc-300">{filteredClubs.length} clubs in your network</p>
            </div>
            <div className="flex gap-3">
              {/* View Toggle */}
              <div className="flex bg-zinc-900/50 border border-white/10 rounded-sm overflow-hidden">
                <button
                  onClick={() => setViewMode('cards')}
                  data-testid="view-mode-cards"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${
                    viewMode === 'cards' ? 'bg-white text-black' : 'text-white hover:bg-white/10'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span className="hidden sm:inline">Cards</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  data-testid="view-mode-list"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${
                    viewMode === 'list' ? 'bg-white text-black' : 'text-white hover:bg-white/10'
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">List</span>
                </button>
              </div>
              <Link
                to="/clubs/new"
                data-testid="add-new-club-button"
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3 transition-all duration-200"
              >
                <Plus className="w-5 h-5" strokeWidth={2} />
                Add Club
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Search */}
        <div className="glass-card p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Search by name, country, or league..."
              data-testid="search-clubs-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
            />
          </div>
        </div>

        {/* Clubs Display */}
        {filteredClubs.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-zinc-500 text-lg">No clubs found. Add your first club to get started!</p>
          </div>
        ) : viewMode === 'cards' ? (
          /* Cards View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClubs.map((club) => (
              <Link
                key={club.id}
                to={`/clubs/${club.id}`}
                data-testid={`club-item-${club.id}`}
                className="glass-card p-6 card-hover group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    {club.badge_url ? (
                      <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden border border-white/10 flex-shrink-0">
                        <img 
                          src={resolveFileUrl(club.badge_url)} 
                          alt={`${club.name} badge`}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => { 
                            e.target.onerror = null; 
                            e.target.style.display = 'none';
                            e.target.parentElement.querySelector('.badge-fallback')?.classList.remove('hidden');
                          }}
                        />
                        <Shield className="w-6 h-6 text-blue-400 hidden badge-fallback" strokeWidth={1.5} />
                      </div>
                    ) : (
                      <div className="p-3 bg-blue-500/10 rounded-lg flex-shrink-0">
                        <Shield className="w-6 h-6 text-blue-400" strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-white mb-1">{club.name}</h3>
                      <p className="text-sm text-zinc-400">
                        {club.league} • {club.country}
                      </p>
                    </div>
                  </div>
                </div>

                {club.contact_person && (
                  <div className="mb-4 p-3 bg-white/5 rounded-lg">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Contact Person</p>
                    <p className="text-sm font-medium text-white">{club.contact_person}</p>
                  </div>
                )}

                <div className="pt-4 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Relationship</p>
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase ${
                          club.relationship_status === 'excellent'
                            ? 'bg-green-500/20 text-green-400'
                            : club.relationship_status === 'good'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-zinc-500/20 text-zinc-400'
                        }`}
                      >
                        {club.relationship_status || 'new'}
                      </span>
                    </div>
                    {club.transfer_budget && (
                      <div className="text-right">
                        <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Budget</p>
                        <p className="text-sm font-bold tabular-nums text-green-400">
                          £{(club.transfer_budget / 1000000).toFixed(1)}M
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-black/30">
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Club</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">League</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Country</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Contact</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Relationship</th>
                  <th className="text-right p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Budget</th>
                </tr>
              </thead>
              <tbody>
                {filteredClubs.map((club) => (
                  <tr
                    key={club.id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => window.location.href = `/clubs/${club.id}`}
                    data-testid={`club-row-${club.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {club.badge_url ? (
                          <div className="w-8 h-8 bg-white/10 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                            <img 
                              src={resolveFileUrl(club.badge_url)} 
                              alt={club.name}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-blue-500/10 rounded flex items-center justify-center flex-shrink-0">
                            <Shield className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
                          </div>
                        )}
                        <span className="font-bold text-white">{club.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-zinc-400">{club.league}</td>
                    <td className="p-4 text-zinc-400">{club.country}</td>
                    <td className="p-4 text-zinc-400">{club.contact_person || '-'}</td>
                    <td className="p-4">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase ${
                          club.relationship_status === 'excellent'
                            ? 'bg-green-500/20 text-green-400'
                            : club.relationship_status === 'good'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-zinc-500/20 text-zinc-400'
                        }`}
                      >
                        {club.relationship_status || 'new'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      {club.transfer_budget ? (
                        <span className="font-bold text-green-400">
                          £{(club.transfer_budget / 1000000).toFixed(1)}M
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clubs;
